import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Button, CircularProgress, Alert, Divider, TextField, InputAdornment, MenuItem,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { ProjectConfig } from 'Global';
import { RANGO_HORARIO_LABELS, CAMPOS_ESPECIFICOS_LABELS, RANGOS_HORARIOS_OPTIONS } from '../../Quotes/quotesConfig';

export default function AdminQuoteView() {
  const { request_id } = useParams<{ request_id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState<any>(null);
  const [closing, setClosing] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<number | null>(null);
  const [editPrecio, setEditPrecio] = useState('');
  const [editInsumos, setEditInsumos] = useState('');
  const [editDisponibilidad, setEditDisponibilidad] = useState('');
  const [editDisponibilidadCustom, setEditDisponibilidadCustom] = useState('');
  const [editNota, setEditNota] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  const fetchData = async () => {
    try {
      const resp = await axios.get(`${ProjectConfig.api_url}/quotes/admin-view`, {
        params: { request_id },
      });
      if (resp.data.status === 'success') {
        setData(resp.data);
      } else {
        setErrorMsg(resp.data.message || 'No se pudo cargar la solicitud');
      }
    } catch (e: any) {
      setErrorMsg('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request_id]);

  const copyNurseLink = () => {
    const link = `${window.location.origin}/ver-cotizacion/${request_id}`;
    navigator.clipboard.writeText(link);
    enqueueSnackbar('Link copiado. Envíalo por WhatsApp a la(s) enfermera(s) de esa comuna.', { variant: 'success' });
  };

  const copyOfferSummary = (offer: any) => {
    const disponibilidad = offer.disponibilidad_horario
      ? `\nDisponibilidad: ${RANGO_HORARIO_LABELS[offer.disponibilidad_horario] || offer.disponibilidad_horario}`
      : '';
    const nota = offer.nota_personalizada ? `\nNota: ${offer.nota_personalizada}` : '';
    const text = `${offer.nurse_name} — $${Number(offer.precio).toLocaleString('es-CL')} CLP\nInsumos: ${offer.insumos_text}${disponibilidad}${nota}`;
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Cotización copiada.', { variant: 'success' });
  };

  const handleClose = async () => {
    if (!window.confirm('¿Cerrar esta solicitud? Ninguna enfermera podrá enviar una nueva cotización después de esto.')) return;
    setClosing(true);
    try {
      const ltkn = localStorage.getItem('ltkn');
      const resp = await axios.post(`${ProjectConfig.api_url}/quotes/close`, { request_id }, { headers: { ltkn } });
      if (resp.data.status === 'success') {
        enqueueSnackbar('Solicitud cerrada.', { variant: 'success' });
        fetchData();
      } else {
        enqueueSnackbar(resp.data.message || 'No se pudo cerrar la solicitud', { variant: 'error' });
      }
    } catch (e: any) {
      const msg = e?.response?.status === 403
        ? 'Debes iniciar sesión como administrador para cerrar una solicitud.'
        : 'Error de conexión al cerrar la solicitud.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setClosing(false);
    }
  };

  const startEdit = (offer: any) => {
    setEditingOfferId(offer.offer_id);
    setEditPrecio(String(offer.precio || ''));
    setEditInsumos(offer.insumos_text || '');
    setEditNota(offer.nota_personalizada || '');
    const savedHorario = offer.disponibilidad_horario || '';
    const isFixedOption = RANGOS_HORARIOS_OPTIONS.some(o => o.value === savedHorario && o.value !== 'personalizado');
    if (savedHorario && !isFixedOption) {
      setEditDisponibilidad('personalizado');
      setEditDisponibilidadCustom(savedHorario);
    } else {
      setEditDisponibilidad(savedHorario);
      setEditDisponibilidadCustom('');
    }
  };

  const cancelEdit = () => {
    setEditingOfferId(null);
    setEditPrecio('');
    setEditInsumos('');
    setEditDisponibilidad('');
    setEditDisponibilidadCustom('');
    setEditNota('');
  };

  const saveEdit = async (offer_id: number) => {
    const disponibilidadFinal = editDisponibilidad === 'personalizado' ? editDisponibilidadCustom.trim() : editDisponibilidad;
    setRegenerating(true);
    try {
      const ltkn = localStorage.getItem('ltkn');
      const resp = await axios.post(
        `${ProjectConfig.api_url}/quotes/admin-regenerate-offer`,
        { offer_id, precio: editPrecio, insumos_text: editInsumos, disponibilidad_horario: disponibilidadFinal, nota_personalizada: editNota },
        { headers: { ltkn } }
      );
      if (resp.data.status === 'success') {
        setData((prev: any) => ({
          ...prev,
          offers: prev.offers.map((o: any) => o.offer_id === offer_id
            ? {
                ...o, precio: resp.data.offer.precio, insumos_text: resp.data.offer.insumos_text,
                disponibilidad_horario: resp.data.offer.disponibilidad_horario, nota_personalizada: resp.data.offer.nota_personalizada,
                image_url: resp.data.image_url,
              }
            : o
          ),
        }));
        enqueueSnackbar('Nueva cotización generada.', { variant: 'success' });
        cancelEdit();
      } else {
        enqueueSnackbar(resp.data.message || 'No se pudo generar la nueva cotización', { variant: 'error' });
      }
    } catch (e: any) {
      const msg = e?.response?.status === 403
        ? 'Debes iniciar sesión como administrador para editar una cotización.'
        : 'Error de conexión al generar la nueva cotización.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (errorMsg || !data) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 6, px: 2 }}>
        <Alert severity="error">{errorMsg || 'Solicitud no encontrada'}</Alert>
      </Box>
    );
  }

  const { request, files, offers } = data;
  const isClosed = !!request.closed_at;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', py: 4, px: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={800} color="#125095">
          Solicitud de enfermera
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${offers.length} cotización${offers.length === 1 ? '' : 'es'} recibida${offers.length === 1 ? '' : 's'}`}
            sx={{ fontWeight: 700, bgcolor: offers.length > 0 ? '#e8f5ee' : '#fff4e5', color: offers.length > 0 ? '#1f7a52' : '#9c6b2e' }}
          />
          {isClosed ? (
            <Chip icon={<LockIcon sx={{ fontSize: 16 }} />} label="Cerrada" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={handleClose}
              disabled={closing}
              sx={{ borderColor: '#B23B3B', color: '#B23B3B', '&:hover': { borderColor: '#8f2e2e', bgcolor: '#fbeaea' } }}
            >
              {closing ? 'Cerrando…' : 'Cerrar cotización'}
            </Button>
          )}
        </Box>
      </Box>

      {isClosed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Esta solicitud está cerrada. Ninguna enfermera nueva podrá enviar una cotización para ella.
        </Alert>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #dde6f5', mb: 2 }}>
        <Typography fontWeight={700} fontSize={16} color="#125095" gutterBottom>
          Datos del paciente
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" sx={{ color: '#888' }} />
            <Typography>{request.nombre} {request.apellido}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon fontSize="small" sx={{ color: '#888' }} />
            <Typography>{request.direccion || 'Sin dirección'} — {request.comuna_name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon fontSize="small" sx={{ color: '#888' }} />
            <Typography>{request.telefono} {request.email ? `— ${request.email}` : ''}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MedicalServicesIcon fontSize="small" sx={{ color: '#888' }} />
            <Chip label={request.procedimiento?.replace(/_/g, ' ')} size="small" sx={{ bgcolor: '#e4eef9', color: '#125095' }} />
          </Box>
          {Object.keys(request.campos_especificos || {}).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3.5 }}>
              {Object.entries(request.campos_especificos).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${CAMPOS_ESPECIFICOS_LABELS[key] || key}: ${value}`}
                  size="small"
                  sx={{ bgcolor: '#f4f7fb', color: '#475569', fontSize: 12 }}
                />
              ))}
            </Box>
          )}
          {request.fecha_deseada && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon fontSize="small" sx={{ color: '#888' }} />
              <Typography>Fecha deseada: {new Date(request.fecha_deseada).toLocaleDateString('es-CL')}</Typography>
            </Box>
          )}
          {request.rango_horario && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon fontSize="small" sx={{ color: '#888' }} />
              <Typography>Horario disponible: {RANGO_HORARIO_LABELS[request.rango_horario] || request.rango_horario}</Typography>
            </Box>
          )}
          {request.descripcion && (
            <Typography color="text.secondary" fontSize={14}>{request.descripcion}</Typography>
          )}
        </Box>
      </Paper>

      {files?.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #dde6f5', mb: 2 }}>
          <Typography fontWeight={700} fontSize={16} color="#125095" gutterBottom>
            Fotografías de la herida
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {files.map((f: any, i: number) => (
              <Box key={i} component="a" href={f.url} target="_blank" rel="noreferrer"
                sx={{ display: 'block', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                <img src={f.url} alt={`herida-${i + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', display: 'block' }} />
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {!isClosed && (
        <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #dde6f5', mb: 2 }}>
          <Typography fontWeight={700} fontSize={16} color="#125095" gutterBottom>
            Reenviar a una enfermera
          </Typography>
          <Typography color="text.secondary" fontSize={14} sx={{ mb: 2 }}>
            Copia este link y envíalo por WhatsApp a la(s) enfermera(s) que cubran {request.comuna_name}. La solicitud queda abierta — pueden cotizar varias.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={copyNurseLink}
            sx={{ bgcolor: '#125095', borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#0e3d78' } }}
          >
            Copiar link para enfermera
          </Button>
        </Paper>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #dde6f5' }}>
        <Typography fontWeight={700} fontSize={16} color="#125095" gutterBottom>
          Cotizaciones recibidas
        </Typography>
        {offers.length === 0 ? (
          <Typography color="text.secondary" fontSize={14}>Aún no llega ninguna cotización.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {offers.map((offer: any, i: number) => (
              <Box key={offer.offer_id}>
                {i > 0 && <Divider sx={{ mb: 2 }} />}

                {editingOfferId === offer.offer_id ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography fontWeight={700}>{offer.nurse_name}</Typography>
                    <TextField
                      label="Precio (CLP)"
                      type="number"
                      size="small"
                      value={editPrecio}
                      onChange={e => setEditPrecio(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                    <TextField
                      label="Descripción de la atención"
                      size="small"
                      multiline
                      rows={4}
                      value={editInsumos}
                      onChange={e => setEditInsumos(e.target.value)}
                    />
                    <TextField
                      select
                      label="Horario de disponibilidad"
                      size="small"
                      value={editDisponibilidad}
                      onChange={e => setEditDisponibilidad(e.target.value)}
                    >
                      {RANGOS_HORARIOS_OPTIONS.map(r => (
                        <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                      ))}
                    </TextField>
                    {editDisponibilidad === 'personalizado' && (
                      <TextField
                        label="Especificar horario"
                        size="small"
                        value={editDisponibilidadCustom}
                        onChange={e => setEditDisponibilidadCustom(e.target.value)}
                      />
                    )}
                    <TextField
                      label="Nota adicional (opcional)"
                      size="small"
                      multiline
                      rows={2}
                      value={editNota}
                      onChange={e => setEditNota(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveEdit(offer.offer_id)}
                        disabled={regenerating}
                        sx={{ bgcolor: '#125095', '&:hover': { bgcolor: '#0e3d78' } }}
                      >
                        {regenerating ? 'Generando…' : 'Generar nueva cotización'}
                      </Button>
                      <Button size="small" variant="text" onClick={cancelEdit} disabled={regenerating}>
                        Cancelar
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography fontWeight={700}>{offer.nurse_name}</Typography>
                      <Typography sx={{ color: '#1f7a52', fontWeight: 700 }}>${Number(offer.precio).toLocaleString('es-CL')} CLP</Typography>
                      <Typography sx={{ whiteSpace: 'pre-line', fontSize: 14, color: 'text.secondary', mt: 0.5 }}>{offer.insumos_text}</Typography>
                      {offer.disponibilidad_horario && (
                        <Typography sx={{ fontSize: 13, color: '#125095', mt: 0.5 }}>
                          Disponibilidad: {RANGO_HORARIO_LABELS[offer.disponibilidad_horario] || offer.disponibilidad_horario}
                        </Typography>
                      )}
                      {offer.nota_personalizada && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, whiteSpace: 'pre-line' }}>
                          Nota: {offer.nota_personalizada}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => startEdit(offer)} sx={{ borderColor: '#125095', color: '#125095' }}>
                        Editar
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copyOfferSummary(offer)} sx={{ borderColor: '#125095', color: '#125095' }}>
                        Copiar
                      </Button>
                      {offer.image_url && (
                        <Button size="small" variant="outlined" startIcon={<ImageIcon />} href={offer.image_url} target="_blank" rel="noreferrer" sx={{ borderColor: '#125095', color: '#125095' }}>
                          Ver imagen
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
